import React from 'react';
import { Badge, Box, Button, Group, Stack, Text } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import type { BaselineRevisionSummary } from '../../../api/baselineApis';
import {
  baselineStatusColor,
  baselineStatusLabel,
  formatBaselineTimestamp,
} from '../logic/baselineHistoryUtils';

export interface BaselineActiveCardProps {
  revision: BaselineRevisionSummary;
  onManageOnGantt: () => void;
}

export function BaselineActiveCard({ revision, onManageOnGantt }: BaselineActiveCardProps) {
  const name = revision.baselineName?.trim() || `Baseline ${revision.baselineIndex}`;
  const meta = [
    `Slot ${revision.baselineIndex}`,
    `Rev ${revision.revisionNumber}`,
    revision.taskCount != null ? `${revision.taskCount} tasks` : null,
    revision.createdBy || null,
    formatBaselineTimestamp(revision.createdAt),
  ].filter(Boolean);

  return (
    <Box
      style={{
        borderRadius: 12,
        border: '1px solid var(--mantine-color-default-border)',
        borderLeft: `3px solid var(--mantine-color-${baselineStatusColor(revision.status)}-6)`,
        background:
          'linear-gradient(180deg, color-mix(in srgb, var(--mantine-color-brand-6) 7%, var(--mantine-color-body)) 0%, var(--mantine-color-body) 100%)',
        padding: '14px 16px',
      }}
    >
      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Group gap={8} wrap="wrap">
            <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: '0.06em' }}>
              Open revision
            </Text>
            <Badge
              size="sm"
              variant="light"
              color={baselineStatusColor(revision.status)}
              radius="sm"
              style={{ textTransform: 'none' }}
            >
              {baselineStatusLabel(revision.status)}
            </Badge>
          </Group>
          <Text fw={700} style={{ fontSize: 16, letterSpacing: '-0.01em' }}>
            {name}
          </Text>
          <Text size="xs" c="dimmed">
            {meta.join(' · ')}
          </Text>
        </Stack>
        <Button
          size="xs"
          variant="light"
          color="brand"
          rightSection={<IconArrowRight size={14} />}
          onClick={onManageOnGantt}
        >
          Manage on Gantt
        </Button>
      </Group>
    </Box>
  );
}
